import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getInventoryAggregated, getClients, getStockHistory } from '../services/api';
import { Warehouse, Search, Package, History, X } from 'lucide-react';

const typeConfig = {
  order_created:     { text: 'Commande créée',      color: 'bg-orange-100 text-orange-700' },
  order_shipped:     { text: 'Commande expédiée',    color: 'bg-cyan-100 text-cyan-700' },
  order_cancelled:   { text: 'Commande annulée',     color: 'bg-red-100 text-red-700' },
  receipt_created:   { text: 'Réception créée',      color: 'bg-blue-100 text-blue-700' },
  receipt_inspected: { text: 'Réception contrôlée',  color: 'bg-green-100 text-green-700' },
  receipt_cancelled: { text: 'Réception annulée',    color: 'bg-red-100 text-red-700' },
  manual_adjustment: { text: 'Ajustement manuel',    color: 'bg-purple-100 text-purple-700' },
};

const formatDelta = (val) => {
  if (!val || val === 0) return <span className="text-slate-400">—</span>;
  return val > 0
    ? <span className="text-green-600 font-medium">+{val}</span>
    : <span className="text-red-600 font-medium">{val}</span>;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

const Inventory = () => {
  const { user } = useAuth();
  const [inventory, setInventory] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [historyModal, setHistoryModal] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    loadData();
    if (user.role === 'admin') loadClients();
  }, [selectedClient, user.role]);

  const loadData = async () => {
    try {
      const clientId = user.role === 'admin' ? selectedClient : user.client_id;
      const response = await getInventoryAggregated({ client_id: clientId });
      setInventory(response.data);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const response = await getClients();
      setClients(response.data);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const openHistory = async (product) => {
    setHistoryLoading(true);
    setHistoryModal({ product, history: [] });
    try {
      const res = await getStockHistory(product.id);
      setHistoryModal({ product, history: res.data });
    } catch (e) {
      console.error('Erreur chargement historique:', e);
    } finally {
      setHistoryLoading(false);
    }
  };

  const filtered = inventory.filter(item =>
    (item.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.sku || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.category || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalQuantity = filtered.reduce((sum, item) => sum + (item.total_stock || 0), 0);
  const totalOnOrder = filtered.reduce((sum, item) => sum + (item.total_on_order || 0), 0);
  const totalIncoming = filtered.reduce((sum, item) => sum + (item.total_incoming || 0), 0);
  const uniqueProducts = filtered.length;

  if (loading) {
    return <div className="flex items-center justify-center min-h-96 text-slate-500">Chargement...</div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="inventory-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Inventaire</h1>
        <p className="text-slate-500">{totalQuantity} unités en stock • {uniqueProducts} produits</p>
      </div>

      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex-1 min-w-80 flex items-center gap-3 bg-white px-4 py-3 rounded-lg border border-slate-200">
          <Search size={20} className="text-slate-400" />
          <input
            type="text"
            placeholder="Rechercher produit, SKU ou catégorie..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 outline-none text-sm"
          />
        </div>
        {user.role === 'admin' && (
          <select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            className="px-4 py-3 border border-slate-200 rounded-lg bg-white text-sm"
          >
            <option value="">Tous les clients</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-xl shadow flex items-center gap-4">
          <Package className="text-blue-500" size={24} />
          <div>
            <div className="text-2xl font-bold text-slate-800">{totalQuantity}</div>
            <div className="text-sm text-slate-500">Unités en stock</div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow flex items-center gap-4">
          <Package className="text-orange-500" size={24} />
          <div>
            <div className="text-2xl font-bold text-slate-800">{totalOnOrder}</div>
            <div className="text-sm text-slate-500">En commande</div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow flex items-center gap-4">
          <Package className="text-cyan-500" size={24} />
          <div>
            <div className="text-2xl font-bold text-slate-800">{totalIncoming}</div>
            <div className="text-sm text-slate-500">En réception</div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow flex items-center gap-4">
          <Warehouse className="text-emerald-500" size={24} />
          <div>
            <div className="text-2xl font-bold text-slate-800">{uniqueProducts}</div>
            <div className="text-sm text-slate-500">Produits référencés</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Produit</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">SKU</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Catégorie</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Stock total</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">En commande</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">En réception</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Seuil min</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Statut</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Client</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item, i) => {
                const stock = item.total_stock || 0;
                const minLevel = item.min_stock_level || 0;
                const outOfStock = stock === 0;
                const lowStock = stock > 0 && stock < minLevel;
                return (
                  <tr key={item.id} className={`border-b border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                    <td className="px-4 py-3 text-sm font-medium text-slate-800">{item.name}</td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs bg-slate-100 px-2 py-1 rounded">{item.sku}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{item.category || '—'}</td>
                    <td className="px-4 py-3">
                      <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm font-medium">{stock}</span>
                    </td>
                    <td className="px-4 py-3">
                      {(item.total_on_order || 0) > 0 ? (
                        <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium">{item.total_on_order}</span>
                      ) : (
                        <span className="text-sm text-slate-400">0</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {(item.total_incoming || 0) > 0 ? (
                        <span className="bg-cyan-100 text-cyan-800 px-3 py-1 rounded-full text-sm font-medium">{item.total_incoming}</span>
                      ) : (
                        <span className="text-sm text-slate-400">0</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">{minLevel}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        outOfStock ? 'bg-red-100 text-red-700' :
                        lowStock ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {outOfStock ? 'Rupture' : lowStock ? 'Stock faible' : 'Disponible'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{item.client_name}</td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => openHistory(item)}
                        className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-blue-600 transition-colors"
                        title="Historique du stock"
                      >
                        <History size={18} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="p-12 text-center">
            <Warehouse size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">Aucun inventaire trouvé</p>
          </div>
        )}
      </div>

      {/* Stock History Modal */}
      {historyModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setHistoryModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Historique du stock</h2>
                <p className="text-sm text-slate-500">{historyModal.product.name} — <span className="font-mono">{historyModal.product.sku}</span></p>
              </div>
              <button onClick={() => setHistoryModal(null)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                <X size={20} className="text-slate-500" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6">
              {historyLoading ? (
                <div className="flex items-center justify-center py-12 text-slate-500">Chargement...</div>
              ) : historyModal.history.length === 0 ? (
                <div className="text-center py-12">
                  <History size={48} className="mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">Aucun historique pour ce produit</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Date</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Type</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-slate-600">Stock</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-slate-600">En commande</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-slate-600">En réception</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-slate-600">Stock après</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Référence</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Par</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Motif</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyModal.history.map((entry, i) => {
                      const cfg = typeConfig[entry.type] || { text: entry.type, color: 'bg-slate-100 text-slate-700' };
                      return (
                        <tr key={entry.id || i} className={`border-b border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                          <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">{formatDate(entry.created_at)}</td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>{cfg.text}</span>
                          </td>
                          <td className="px-3 py-2 text-center text-sm">{formatDelta(entry.quantity_delta)}</td>
                          <td className="px-3 py-2 text-center text-sm">{formatDelta(entry.on_order_delta)}</td>
                          <td className="px-3 py-2 text-center text-sm">{formatDelta(entry.incoming_delta)}</td>
                          <td className="px-3 py-2 text-center text-sm font-medium text-slate-700">{entry.quantity_after ?? '—'}</td>
                          <td className="px-3 py-2 text-xs text-slate-600">{entry.reference_number || '—'}</td>
                          <td className="px-3 py-2">
                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${entry.performed_by_role === 'admin' ? 'bg-indigo-100 text-indigo-700' : 'bg-teal-100 text-teal-700'}`}>
                              {entry.performed_by_role === 'admin' ? 'Admin' : 'Client'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-xs text-slate-500 max-w-32 truncate" title={entry.reason || ''}>{entry.reason || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;
